import './ActiveOTT.scss';
import {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdOutlineFileDownload } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { formatMoney, formatMoneyAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DatePicker } from '@Components/DatePicker';
import { Checkbox } from '@Components/FormCheck';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { IconButton } from '@Components/IconButton';
import { Img } from '@Components/Img';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Page } from '@Components/Page';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { CellRenderer, CornerLocation, StickyTable } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useSetError } from '../../redux/modals';
import { useStateFunction } from '../../utils/hooks/useData';
import {
  ActiveOTTContextValue,
  BASE_PATH,
  CellData,
  COLUMN_SPACER_WIDTH,
  COLUMN_WIDTH,
  COMPANY_FILTER_OPTIONS,
  CompanyOption,
  DateRange,
  DimensionsState,
  Header,
  HEADER_HEIGHT,
  HeaderMetaData,
  NETWORK_FILTER_OPTIONS,
  NetworkOptions,
  RIGHT_HEADER_WIDTH,
  RightTotal,
  ROW_HEIGHT
} from './active-ott-constants';
import { ActiveOTTContext, useActiveOTTContext } from './active-ott-context';
import {
  getIsCurrentWeek,
  getPrettyWeek,
  initDates,
  makeCPM,
  transformAndDownloadDataToCSV
} from './active-ott-utils';
import { DetailModal } from './DetailModal';
import {
  useActiveOttCompanyOptions,
  useActiveOttData,
  useActiveOttExpectedBookings,
  useActiveOttNetworkOptions
} from './use-active-ott';

const useDimensions = (): [DimensionsState, (type: keyof DimensionsState) => void] => {
  const [dimensions, setDimensions] = useState<DimensionsState>({
    spend: true,
    cpm: false
  });

  const toggleDimension = useCallback((type: keyof DimensionsState) => {
    setDimensions(prev => {
      // If the button they pressed is set, it should become unset. If the button they pressed is unset, it should
      // become set. No matter what, the button they click on should toggle.
      // If only the pressed button was on, then we swap and make the other one turn on (so this will be true). If the
      // other button was already on, clicking this one has no effect: either you turn it on and you have two things on,
      // or you turn it off and you only have the other one on. In this case it stays true. So no matter what the
      // orientation is, pressing a button makes the other one true
      if (type === 'cpm') {
        return { cpm: !prev.cpm, spend: true };
      }
      return { spend: !prev.spend, cpm: true };
    });
  }, []);
  return [dimensions, toggleDimension];
};

const usePageState = ({
  networkOptions,
  companyOptions,
  path
}: {
  networkOptions?: NetworkOptions;
  companyOptions?: CompanyOption[];
  path: string;
}): {
  byNetwork: boolean;
  selectedGroup: CompanyOption | string | undefined;
  setByNetwork: (byNetwork: boolean) => void;
  setSelectedGroup: (key: string) => void;
} => {
  const navigate = useNavigate();

  const [byNetwork, urlSelection] = useMemo(() => {
    const pathParts = path.split('/').filter(Boolean);
    if (pathParts[0] === 'networks') {
      return [true, pathParts[1]] as const;
    }
    return [false, pathParts[0]] as const;
  }, [path]);

  const selectedGroup = useMemo<CompanyOption | string | undefined>(() => {
    if (byNetwork) {
      if (!networkOptions) {
        return undefined;
      }
      const networks = Object.keys(networkOptions);
      return urlSelection && networks.includes(urlSelection) ? urlSelection : networks[0];
    }
    if (!companyOptions) {
      return undefined;
    }
    return companyOptions.find(option => option.cid === urlSelection) ?? companyOptions[0];
  }, [byNetwork, urlSelection, companyOptions, networkOptions]);

  const setByNetwork = useCallback(
    (nextByNetwork: boolean) => {
      navigate(nextByNetwork ? `${BASE_PATH}/networks` : BASE_PATH);
    },
    [navigate]
  );
  const setSelectedGroup = useCallback(
    (key: string) => {
      navigate(byNetwork ? `${BASE_PATH}/networks/${key}` : `${BASE_PATH}/${key}`);
    },
    [byNetwork, navigate]
  );

  return { byNetwork, selectedGroup, setByNetwork, setSelectedGroup };
};

const bottomRenderer: CellRenderer<number> = ({ data, style, classes }) => (
  <div className={cn(...classes, 'active-ott-bottom-cell')} style={style}>
    {formatMoneyAsInt(data)}
  </div>
);

interface ActiveOTTGroupToggleProps {
  byNetwork: boolean;
  setByNetwork: (byNetwork: boolean) => void;
}

const ActiveOTTGroupToggle = ({ byNetwork, setByNetwork }: ActiveOTTGroupToggleProps) => (
  <SegmentedControlButton
    size={Size.LG}
    options={[
      { key: 'company', label: 'Company' },
      { key: 'network', label: 'Network' }
    ]}
    selectedOption={byNetwork ? 'network' : 'company'}
    onChange={key => setByNetwork(key === 'network')}
  />
);

const ActiveOTTGroupPicker = (): ReactElement => {
  const { byNetwork, selectedGroup, setSelectedGroup, companyOptions, networkOptions } =
    useActiveOTTContext();

  const options = useMemo(
    () =>
      byNetwork
        ? Object.keys(networkOptions ?? {}).map(value => ({ value, label: value }))
        : (companyOptions ?? []).map(option => ({ value: option.cid, label: option.cid })),
    [byNetwork, networkOptions, companyOptions]
  );
  const value = typeof selectedGroup === 'string' ? selectedGroup : selectedGroup.cid;

  return (
    <Dropdown
      size={Size.LG}
      searchable
      value={value}
      options={options}
      onChange={newValue => {
        if (newValue) {
          setSelectedGroup(newValue);
        }
      }}
    />
  );
};

interface ActiveOTTPageActionsProps {
  byNetwork: boolean;
  hasOptions: boolean;
  selectedGroup: CompanyOption | string | undefined;
  dimensions: DimensionsState;
  toggleDimension: (type: keyof DimensionsState) => void;
  setByNetwork: (byNetwork: boolean) => void;
  includeAudio: boolean;
  setIncludeAudio: (fn: (prev: boolean) => boolean) => void;
  includeOLV: boolean;
  setIncludeOLV: (fn: (prev: boolean) => boolean) => void;
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  rows?: CellData[][];
  filteredHeaders: Header[];
  rightTotals: RightTotal[];
}

const ActiveOTTPageActions = ({
  byNetwork,
  hasOptions,
  selectedGroup,
  dimensions,
  toggleDimension,
  setByNetwork,
  includeAudio,
  setIncludeAudio,
  includeOLV,
  setIncludeOLV,
  dates,
  setDates,
  rows,
  filteredHeaders,
  rightTotals
}: ActiveOTTPageActionsProps): ReactElement => (
  <div className='active-ott-actions'>
    <div className='active-ott-actions-dropdown' data-testid='active-ott-group-picker'>
      {selectedGroup && hasOptions && <ActiveOTTGroupPicker />}
    </div>
    {!byNetwork && (
      <SegmentedControlButton
        size={Size.LG}
        isExclusiveSelect={false}
        options={[
          { key: 'audio', label: 'Audio' },
          { key: 'olv', label: 'OLV' }
        ]}
        selectedOptions={[...(includeAudio ? ['audio'] : []), ...(includeOLV ? ['olv'] : [])]}
        onChange={key => {
          if (key === 'audio') {
            setIncludeAudio(prev => !prev);
          } else {
            setIncludeOLV(prev => !prev);
          }
        }}
      />
    )}
    <SegmentedControlButton
      size={Size.LG}
      isExclusiveSelect={false}
      options={[
        { key: 'spend', label: 'Spend' },
        { key: 'cpm', label: 'CPM' }
      ]}
      selectedOptions={[...(dimensions.spend ? ['spend'] : []), ...(dimensions.cpm ? ['cpm'] : [])]}
      onChange={key => toggleDimension(key as keyof DimensionsState)}
    />
    <ActiveOTTGroupToggle byNetwork={byNetwork} setByNetwork={setByNetwork} />
    <DatePicker range={dates} onChange={setDates} isOutsideRange={() => false} />
    <IconButton
      data-testid='active-ott-download-button'
      tooltip={{ text: 'Download CSV' }}
      disabled={!rows}
      onClick={() => {
        if (!rows) {
          return;
        }
        transformAndDownloadDataToCSV({
          rows,
          filteredHeaders,
          rightTotals,
          byNetwork,
          isCpm: dimensions.cpm,
          isSpend: dimensions.spend,
          fileName: `${
            typeof selectedGroup === 'string' ? selectedGroup : selectedGroup?.cid
          } Active OTT`
        });
      }}
    >
      <MdOutlineFileDownload />
    </IconButton>
  </div>
);

interface ActiveOTTFilterControlsProps {
  useOverrides: boolean;
  setUseOverrides: (fn: (prev: boolean) => boolean) => void;
  includeFees: boolean;
  setIncludeFees: (fn: (prev: boolean) => boolean) => void;
  includeCommission: boolean;
  setIncludeCommission: (fn: (prev: boolean) => boolean) => void;
  filterOptions: FilterColumn[];
  filterLines: HeaderMetaData[];
  setFilter: (filter: (metaData: HeaderMetaData) => boolean) => void;
}

const ActiveOTTFilterControls = ({
  useOverrides,
  setUseOverrides,
  includeFees,
  setIncludeFees,
  includeCommission,
  setIncludeCommission,
  filterOptions,
  filterLines,
  setFilter
}: ActiveOTTFilterControlsProps): ReactElement => (
  <div className='active-ott-filter-bar-area' data-testid='active-ott-filter-bar'>
    <div className='active-ott-toggles'>
      <div className='active-ott-toggle-with-info'>
        <Checkbox
          id='active-ott-pdf-numbers'
          label='PDF Numbers'
          checked={useOverrides}
          onChange={() => setUseOverrides(prev => !prev)}
        />
        <InfoTooltip placement={Placement.RIGHT}>
          When enabled, this uses $0 for the spend when the flight is canceled, or any wrenched PDF
          overrides, if present.
        </InfoTooltip>
      </div>
      <Checkbox
        id='active-ott-include-fees'
        label='Include Fees'
        checked={includeFees}
        onChange={() => setIncludeFees(prev => !prev)}
      />
      <Checkbox
        id='active-ott-include-commission'
        label='Include Commission'
        checked={includeCommission}
        onChange={() => setIncludeCommission(prev => !prev)}
      />
    </div>
    <FilterBar
      size={Size.LG}
      hasAdvanced
      options={filterOptions}
      lines={filterLines}
      onFilter={setFilter}
    />
  </div>
);

export const ActiveOTT = (): ReactElement => {
  const { '*': path = '' } = useParams();
  const setError = useSetError();

  const [dimensions, toggleDimension] = useDimensions();
  const [dates, setDates] = useState<DateRange>(initDates);

  const [filter, setFilter] = useStateFunction<(metaData: HeaderMetaData) => boolean>(() => true);

  const networkOptionsQuery = useActiveOttNetworkOptions(dates);
  const companyOptionsQuery = useActiveOttCompanyOptions();
  const networkOptions = networkOptionsQuery.data;
  const companyOptions = companyOptionsQuery.data;

  const { byNetwork, selectedGroup, setByNetwork, setSelectedGroup } = usePageState({
    networkOptions,
    companyOptions,
    path
  });

  const [useOverrides, setUseOverrides] = useState(false);

  const expectedBookingsQuery = useActiveOttExpectedBookings(
    dates,
    byNetwork || typeof selectedGroup !== 'object' ? false : selectedGroup.cid
  );
  const expectedBookings = expectedBookingsQuery.data;

  const [includeCommission, setIncludeCommission] = useState(false);
  const [includeFees, setIncludeFees] = useState(false);
  const [includeAudio, setIncludeAudio] = useState(false);
  const [includeOLV, setIncludeOLV] = useState(false);

  const hasOptions = Boolean(byNetwork ? networkOptions : companyOptions);

  const activeOttDataQuery = useActiveOttData({ dates, selectedGroup, byNetwork });
  const { headers, weeks, flightMap } = activeOttDataQuery.data ?? {};

  const queryError =
    companyOptionsQuery.error ||
    networkOptionsQuery.error ||
    expectedBookingsQuery.error ||
    activeOttDataQuery.error;
  useEffect(() => {
    if (queryError) {
      setError({ message: queryError.message, reportError: queryError });
    }
  }, [queryError, setError]);

  const networkHeaderLines = useMemo(() => {
    if (!byNetwork || !weeks || !headers) {
      return {};
    }
    const headerLineMap: Record<string, Record<string, HeaderMetaData>> = {};
    for (const { data } of weeks) {
      for (const { cid } of headers) {
        if (!cid) {
          continue;
        }
        const cellLines = data[cid];
        if (!cellLines) {
          continue;
        }
        headerLineMap[cid] ??= {};
        for (const { metaData } of cellLines) {
          const obj = { ...metaData, cid };
          headerLineMap[cid][JSON.stringify(obj)] = obj;
        }
      }
    }
    return Object.fromEntries(
      Object.entries(headerLineMap).map(([cid, lines]) => [cid, Object.values(lines)])
    );
  }, [weeks, byNetwork, headers]);

  // Get the lines for the filter bar, and a special filter function. For company mode, the lines
  // are just the headers. For network mode, the filter bar not only filters the headers, but the
  // data (by platform and description). Since one filter bar filters two data sets, we need to
  // construct "lines" to give the filter bar so it knows what the valid options are for each field.
  // Since the valid options only need to appear once for each field, we can just zip the two lists
  // together arbitrarily, duplicating the last element for the remaining values of the longer list.
  // Additionally, if you enter a filter for one field and not the other, every element from the
  // other data set will be filtered out, since none of them have the properties from the other data
  // set (and, thus, won't match). So we provide a custom filter function. We can use a
  // previously-constructed map (networkHeaderLines), which maps a company to all the
  // platform/descriptions it contains. Thus, we can filter on that.
  const [filterLines, modifiedFilter] = useMemo((): [
    HeaderMetaData[],
    (el: Header) => [boolean, boolean]
  ] => {
    if (!headers) {
      return [[], () => [true, false]];
    }
    let lines: HeaderMetaData[] = [];
    if (!byNetwork) {
      lines = headers;
    } else if (typeof selectedGroup === 'string') {
      const networkLines = Object.values(networkOptions?.[selectedGroup] ?? {}).flat();
      const lineCount = Math.max(networkLines.length, headers.length);
      for (let i = 0; i < lineCount; ++i) {
        lines.push({
          ...(i >= headers.length ? headers[headers.length - 1] : headers[i]),
          ...(i >= networkLines.length ? networkLines[networkLines.length - 1] : networkLines[i])
        });
      }
    }
    return [
      lines,
      el => {
        if (!byNetwork) {
          if (!includeAudio && el.platform.includes('Audio')) {
            return [false, false];
          }
          if (!includeOLV && el.platform.includes('OLV')) {
            return [false, false];
          }
        }
        if (byNetwork) {
          const filtered = el.cid ? !(networkHeaderLines[el.cid] ?? []).some(filter) : true;
          return filtered ? [false, true] : [true, false];
        }
        const include = filter(el);
        return [include, !include];
      }
    ];
  }, [
    headers,
    selectedGroup,
    byNetwork,
    networkOptions,
    filter,
    includeAudio,
    includeOLV,
    networkHeaderLines
  ]);

  const [filteredHeaders, filterOptions, isFiltered] = useMemo((): [
    Header[],
    FilterColumn[],
    boolean
  ] => {
    if (!headers) {
      return [[], COMPANY_FILTER_OPTIONS, false];
    }

    const options = byNetwork ? NETWORK_FILTER_OPTIONS : COMPANY_FILTER_OPTIONS;

    let anyFiltered = false;
    const sortKeys = options
      .map(({ name }) => name)
      .filter((name): name is keyof Header => Boolean(name));
    const filtered = headers
      .filter(el => {
        const [include, ourIsFiltered] = modifiedFilter(el);
        anyFiltered = anyFiltered || ourIsFiltered;
        return include;
      })
      .sort((a, b) => {
        for (const key of sortKeys) {
          const aVal = String(a[key] ?? '');
          const bVal = String(b[key] ?? '');
          if (aVal !== bVal) {
            return aVal < bVal ? -1 : 1;
          }
        }
        return 0;
      });
    return [filtered, options, anyFiltered];
  }, [headers, byNetwork, modifiedFilter]);

  const [rows, weekVals] = useMemo((): [CellData[][] | undefined, string[] | undefined] => {
    // Only `!weeks` means "not loaded yet" — an empty filteredHeaders array is a valid
    // loaded-but-empty state (e.g. a client with no networks) and must still produce `[]`,
    // not `undefined`, or the page hangs on the loading skeleton forever.
    if (!weeks) {
      return [undefined, undefined];
    }

    const newWeekVals: string[] = [];
    const newRows: CellData[][] = [];
    for (const { week, data } of weeks) {
      newWeekVals.push(week);
      const row: CellData[] = [];
      for (const header of filteredHeaders) {
        if (!byNetwork) {
          if (!includeAudio && header.platform.includes('Audio')) {
            continue;
          }
          if (!includeOLV && header.platform.includes('OLV')) {
            continue;
          }
        }
        const dataRows = data[header.key];
        if (!dataRows) {
          row.push({ week });
          continue;
        }
        const cellData = {
          spend: 0,
          adServingFee: 0,
          dspCpmFee: 0,
          dspCommissionFee: 0,
          commission: 0,
          impressions: 0,
          flights: [] as string[][]
        };
        for (const dataRow of dataRows) {
          const metaData: HeaderMetaData = byNetwork
            ? { ...dataRow.metaData, cid: header.cid }
            : dataRow.metaData;
          if (!filter(metaData)) {
            continue;
          }

          cellData.flights.push(dataRow.flights);
          const ourData = useOverrides && dataRow.overrides ? dataRow.overrides : dataRow;
          cellData.spend += ourData.media;
          cellData.adServingFee += ourData.adServingFee;
          cellData.dspCpmFee += ourData.dspCpmFee;
          cellData.dspCommissionFee += ourData.dspCommissionFee;
          cellData.commission += ourData.agencyCommission;
          cellData.impressions += ourData.impressions;
          if (includeCommission) {
            cellData.spend += ourData.agencyCommission;
          }
          if (includeFees) {
            // Only add cpm-based dsp fees, since commission-based dsp fees are
            // already baked into the media spend.
            cellData.spend += ourData.adServingFee + ourData.dspCpmFee;
          }
        }

        row.push({
          week,
          flights: [...new Set(cellData.flights.flat())].sort(),
          spend: Math.round(cellData.spend),
          adServingFee: Math.round(cellData.adServingFee),
          dspCpmFee: Math.round(cellData.dspCpmFee),
          dspCommissionFee: Math.round(cellData.dspCommissionFee),
          commission: Math.round(cellData.commission),
          includeFees,
          includeCommission,
          cpm: makeCPM(cellData.spend, cellData.impressions)
        });
      }
      if (row.length) {
        newRows.push(row);
      }
    }
    return [newRows, newWeekVals];
  }, [
    weeks,
    filteredHeaders,
    includeAudio,
    includeOLV,
    byNetwork,
    useOverrides,
    includeCommission,
    includeFees,
    filter
  ]);

  const bottomTotals = useMemo(() => {
    if (!rows?.length) {
      return [];
    }
    const colCount = rows[0].length;
    const totals = new Array(colCount).fill(0);
    for (const row of rows) {
      for (let i = 0; i < colCount; ++i) {
        totals[i] += row[i]?.spend ?? 0;
      }
    }
    return totals;
  }, [rows]);

  const stickyTableScrollToRef = useRef<(row: number, col: number) => void>(null);
  // When data changes, it scrolls to the current week
  useLayoutEffect(() => {
    if (rows && stickyTableScrollToRef.current) {
      for (let i = 0; i < rows.length; ++i) {
        if (getIsCurrentWeek(rows[i][0]?.week)) {
          stickyTableScrollToRef.current(i, 0);
          break;
        }
      }
    }
  }, [rows]);

  const bothDimensions = dimensions.cpm && dimensions.spend;

  const [modalData, setModalData] = useState<CellData>();

  const cellRenderer: CellRenderer<CellData> = useCallback(
    ({ data, style, classes }) => {
      classes.push('active-ott-cell');
      if (getIsCurrentWeek(data.week)) {
        classes.push('current-week');
      }
      const hasVals = Object.keys(data).length > 1;

      const [spend, cpm] = ['spend', 'cpm'].map(key => {
        if (!hasVals) {
          return '-';
        }
        const val = dimensions[key as keyof DimensionsState]
          ? data[key as 'spend' | 'cpm']
          : undefined;
        if (val === undefined) {
          return '-';
        }
        return key === 'spend' ? formatMoneyAsInt(val) : formatMoney(val);
      });

      if (hasVals) {
        classes.push('has-vals');
      }

      return (
        <InteractiveDiv
          className={cn(...classes)}
          style={style}
          onClick={hasVals ? () => setModalData(data) : undefined}
          disabled={!hasVals}
        >
          {dimensions.spend && <div>{spend}</div>}
          {bothDimensions && <div className='spacer' style={{ minWidth: COLUMN_SPACER_WIDTH }} />}
          {dimensions.cpm && <div>{cpm}</div>}
        </InteractiveDiv>
      );
    },
    [dimensions, bothDimensions]
  );

  const rightTotals = useMemo((): RightTotal[] => {
    if (!weekVals) {
      return [];
    }
    const totals: RightTotal[] = [];

    for (let weekIndex = 0; weekIndex < weekVals.length; ++weekIndex) {
      const week = weekVals[weekIndex];
      const elem: RightTotal = { week, spend: 0, expected: 0 };
      const weekExpected = expectedBookings?.[week];
      if (weekExpected) {
        let expectedVal = 0;
        const mediaTypes: ('streaming' | 'audio')[] = ['streaming'];
        if (includeAudio) {
          mediaTypes.push('audio');
        }
        for (const type of mediaTypes) {
          const entry = weekExpected[type];
          if (entry) {
            expectedVal += entry.media;
            if (includeFees) {
              // Only add cpm-based dsp fees, since commission-based dsp fees are
              // already baked into the media spend.
              expectedVal += entry.adServingFee + entry.dspCpmFee;
            }
            if (includeCommission) {
              expectedVal += entry.agencyCommission;
            }
          }
        }
        elem.expected = Math.round(expectedVal);
      }
      for (let headerIndex = 0; headerIndex < filteredHeaders.length; ++headerIndex) {
        elem.spend += rows?.[weekIndex]?.[headerIndex]?.spend ?? 0;
      }
      totals.push(elem);
    }
    return totals;
  }, [
    weekVals,
    rows,
    expectedBookings,
    includeFees,
    includeCommission,
    includeAudio,
    filteredHeaders
  ]);

  const showExpectedBookings = Boolean(expectedBookings) && !byNetwork && !isFiltered;
  const rightWidth = showExpectedBookings ? RIGHT_HEADER_WIDTH * 2 : RIGHT_HEADER_WIDTH;
  const columnWidth = bothDimensions ? COLUMN_WIDTH * 2 + COLUMN_SPACER_WIDTH : COLUMN_WIDTH;

  const cornerRenderer = useCallback(
    (corner: CornerLocation) => {
      if (corner !== 'ne') {
        return <span />;
      }
      return (
        <div className='active-ott-right-headers'>
          {showExpectedBookings && <div>Expected</div>}
          <div>Total</div>
        </div>
      );
    },
    [showExpectedBookings]
  );

  const leftRenderer: CellRenderer<string> = useCallback(({ data, classes, style }) => {
    classes.push('active-ott-left-cell');
    if (getIsCurrentWeek(data)) {
      classes.push('current-week');
    }

    return (
      <div className={cn(...classes)} style={style}>
        {getPrettyWeek(data)}
      </div>
    );
  }, []);

  const rightRenderer: CellRenderer<RightTotal> = useCallback(
    ({ data, style, classes }) => {
      classes.push('active-ott-right-column');
      if (getIsCurrentWeek(data.week)) {
        classes.push('current-week');
      }

      return (
        <div className={cn(...classes)} style={style}>
          {showExpectedBookings && (
            <div>{data.expected ? formatMoneyAsInt(data.expected) : '-'}</div>
          )}
          <div>{data.spend ? formatMoneyAsInt(data.spend) : '-'}</div>
        </div>
      );
    },
    [showExpectedBookings]
  );

  const topRenderer: CellRenderer<Header> = useCallback(
    ({ data, style, classes }) => (
      <OverlayTrigger
        placement={Placement.BOTTOM}
        overlay={
          <Tooltip>
            {byNetwork
              ? data.cid
              : `${data.shortCode}${data.description ? ` ${data.description}` : ''} ${data.platform}`}
          </Tooltip>
        }
      >
        <div className={cn(...classes, 'active-ott-header', 'company-logo-header')} style={style}>
          <div className='logo'>
            <Img
              src={`https://cdn.blisspointmedia.com/${
                byNetwork
                  ? `companies/${data.cid}/${bothDimensions ? 'logo' : 'square_logo'}`
                  : `networks/${data.shortCode}`
              }.png`}
              unloader={byNetwork ? data.cid : data.shortCode}
            />
          </div>
          {!byNetwork && <div className='platform'>{data.platform.replace('Streaming ', '')}</div>}
          {!byNetwork && data.description && <div className='description'>{data.description}</div>}
        </div>
      </OverlayTrigger>
    ),
    [byNetwork, bothDimensions]
  );

  const contextValue = useMemo<ActiveOTTContextValue>(
    () => ({
      byNetwork,
      selectedGroup: selectedGroup ?? '',
      setSelectedGroup,
      networkOptions,
      companyOptions,
      flightMap
    }),
    [byNetwork, selectedGroup, setSelectedGroup, networkOptions, companyOptions, flightMap]
  );

  return (
    <ActiveOTTContext.Provider value={contextValue}>
      <Page
        title='Active OTT'
        pageType='Active OTT'
        minHeight='600px'
        minWidth='1240px'
        isLoading={!rows}
        actions={
          <ActiveOTTPageActions
            byNetwork={byNetwork}
            hasOptions={hasOptions}
            selectedGroup={selectedGroup}
            dimensions={dimensions}
            toggleDimension={toggleDimension}
            setByNetwork={setByNetwork}
            includeAudio={includeAudio}
            setIncludeAudio={setIncludeAudio}
            includeOLV={includeOLV}
            setIncludeOLV={setIncludeOLV}
            dates={dates}
            setDates={setDates}
            rows={rows}
            filteredHeaders={filteredHeaders}
            rightTotals={rightTotals}
          />
        }
      >
        <div className='active-ott' data-testid='active-ott-page'>
          {rows ? (
            <>
              <ActiveOTTFilterControls
                useOverrides={useOverrides}
                setUseOverrides={setUseOverrides}
                includeFees={includeFees}
                setIncludeFees={setIncludeFees}
                includeCommission={includeCommission}
                setIncludeCommission={setIncludeCommission}
                filterOptions={filterOptions}
                filterLines={filterLines}
                setFilter={setFilter}
              />
              {rows.length ? (
                <div className='active-ott-table' data-testid='active-ott-table'>
                  <AutoSizer
                    renderProp={({ width = 0, height = 0 }) => {
                      // Stretch columns/rows to fill the available space when there isn't much
                      // data (few columns/weeks); fall back to the fixed size (with scrolling)
                      // once there's enough to fill it anyway. StickyTable's own fill-when-smaller
                      // logic only resizes its internal container aggregate, not the actual
                      // per-cell width/height (those come straight from the columnWidth/rowHeight
                      // props below, via useVirtual) — so the stretch has to happen here.
                      const numCols = filteredHeaders.length || 1;
                      const dynColumnWidth = Math.max(
                        columnWidth,
                        (width - 75 - rightWidth) / numCols
                      );
                      const numRows = weekVals?.length || 1;
                      const dynRowHeight = Math.max(
                        ROW_HEIGHT,
                        (height - HEADER_HEIGHT) / (numRows + 1)
                      );

                      return (
                        <StickyTable
                          scrollToRef={stickyTableScrollToRef}
                          alternateColors={false}
                          width={width}
                          height={height}
                          data={rows}
                          topData={filteredHeaders}
                          leftData={weekVals}
                          rightData={rightTotals}
                          bottomData={bottomTotals}
                          leftWidth={75}
                          rowHeight={dynRowHeight}
                          topHeight={HEADER_HEIGHT}
                          rightWidth={rightWidth}
                          columnWidth={dynColumnWidth}
                          cornerRenderer={cornerRenderer}
                          cellRenderer={cellRenderer}
                          leftRenderer={leftRenderer}
                          rightRenderer={rightRenderer}
                          topRenderer={topRenderer}
                          bottomRenderer={bottomRenderer}
                        />
                      );
                    }}
                  />
                  {modalData && (
                    <DetailModal data={modalData} onClose={() => setModalData(undefined)} />
                  )}
                </div>
              ) : (
                <div className='active-ott-empty-state' data-testid='active-ott-empty-state'>
                  <TableEmptyState message='This client has no spend data with this filter in this date range.' />
                </div>
              )}
            </>
          ) : (
            <Skeleton>
              <TableSkeleton rowHeight={ROW_HEIGHT} headerHeight={HEADER_HEIGHT} />
            </Skeleton>
          )}
        </div>
      </Page>
    </ActiveOTTContext.Provider>
  );
};
